import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { fetchUserLibrary, LibraryServiceError } from '../services/libraryService';
import type { RentalLibraryItem } from '../types/firestore.types';

export type { RentalLibraryItem };

const CACHE_KEY = 'guidero_library_cache_v2';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface LibraryCacheData {
  items: RentalLibraryItem[];
  lastFetched: number;
  userId: string | null;
}

interface LibraryContextType {
  items: RentalLibraryItem[];
  isLoading: boolean;
  error: string | null;
  loadLibrary: (forceRefresh?: boolean) => Promise<void>;
  getItemById: (gboxId: string) => RentalLibraryItem | undefined;
  clearCache: () => void;
}

const LibraryContext = createContext<LibraryContextType | undefined>(undefined);

function loadFromStorage(): LibraryCacheData | null {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached) as LibraryCacheData;
      // Restore Date objects
      data.items = data.items.map((item) => ({
        ...item,
        contractExpires: new Date(item.contractExpires),
      }));
      return data;
    }
  } catch {
    // ignore
  }
  return null;
}

function saveToStorage(data: LibraryCacheData): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

function clearStorage(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

export function LibraryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<RentalLibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<number>(0);

  // Mirror state into refs so loadLibrary can read current values without
  // listing them as dependencies (which would change its identity every fetch
  // and re-fire the consuming effect in an infinite loop).
  const lastFetchedRef = useRef(lastFetched);
  lastFetchedRef.current = lastFetched;

  // Load from cache on mount
  useEffect(() => {
    const cached = loadFromStorage();
    if (cached && cached.userId === user?.uid) {
      const age = Date.now() - cached.lastFetched;
      if (age < CACHE_TTL_MS) {
        setItems(cached.items);
        setLastFetched(cached.lastFetched);
      }
    }
  }, [user?.uid]);

  // Clear cache when user changes
  useEffect(() => {
    const cached = loadFromStorage();
    if (cached && cached.userId !== user?.uid) {
      clearStorage();
      setItems([]);
      setLastFetched(0);
    }
  }, [user?.uid]);

  const clearCache = useCallback(() => {
    clearStorage();
    setItems([]);
    setLastFetched(0);
    setError(null);
  }, []);

  const loadLibrary = useCallback(
    async (forceRefresh = false) => {
      if (!user?.uid) {
        setError('Authentication required');
        return;
      }

      // Use cache if still valid. Gate on lastFetched (not items.length) so an
      // empty library is also treated as loaded and does not refetch forever.
      if (!forceRefresh && lastFetchedRef.current > 0) {
        const age = Date.now() - lastFetchedRef.current;
        if (age < CACHE_TTL_MS) return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchUserLibrary(user.uid);
        const now = Date.now();
        setItems(result);
        setLastFetched(now);
        saveToStorage({ items: result, lastFetched: now, userId: user.uid });
      } catch (err) {
        if (err instanceof LibraryServiceError) {
          setError(err.message);
        } else {
          setError('Failed to load library');
        }
        console.error('[LibraryContext] Failed to load library:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.uid],
  );

  const getItemById = useCallback(
    (gboxId: string): RentalLibraryItem | undefined =>
      items.find((item) => item.gboxId === gboxId),
    [items],
  );

  const value = useMemo<LibraryContextType>(
    () => ({ items, isLoading, error, loadLibrary, getItemById, clearCache }),
    [items, isLoading, error, loadLibrary, getItemById, clearCache],
  );

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}

export function useLibrary(): LibraryContextType {
  const context = useContext(LibraryContext);
  if (context === undefined) {
    throw new Error('useLibrary must be used within a LibraryProvider');
  }
  return context;
}

export default LibraryContext;
